import React from 'react';
import { useAssessmentData } from './AssessmentDataContext';
import { SectionHeader, ScrollTable, BucketPill, DeltaBadge, MIN_RELIABLE_N, fmtPct, returnColor } from './shared';

// Extracted verbatim from Assessment.js's ReturnsTable component + its
// `activeTab === 'returns'` render block (Tech Debt 2026-09, "Assessment and
// Backtest are god files" -- remaining-7-tabs context lift). Reads shared
// state from useAssessmentData() instead of via props.

function ReturnsTable({ buckets, prevByBucket = {} }) {
  const displayPeriods = ['3d', '5d', '7d', '15d', '30d', '60d', '90d'];
  return (
    <ScrollTable minWidth={620}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08]">
            <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-20">Bucket</th>
            <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-12">N</th>
            {displayPeriods.map(p => (
              <th key={p} colSpan={2} className="text-center py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 border-l border-white/[0.06]">
                {p}
              </th>
            ))}
          </tr>
          <tr className="border-b border-white/[0.06]">
            <th className="py-1 px-3" />
            <th className="py-1 px-2" />
            {displayPeriods.map(p => (
              <React.Fragment key={p}>
                <th className="py-1 px-2 text-right text-gray-700 text-[10px] font-normal border-l border-white/[0.04] min-w-[52px]">Ret</th>
                <th className="py-1 px-2 text-right text-gray-700 text-[10px] font-normal min-w-[52px]">MFE</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {buckets.map((row) => {
            const prev = prevByBucket[row.bucket];
            return (
              <tr
                key={row.bucket}
                className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
              >
                <td className="py-2 px-3"><BucketPill bucket={row.bucket} /></td>
                <td className="py-2 px-2 text-right text-gray-500 font-mono tabular-nums text-[11px] whitespace-nowrap">
                  {row.sample_count}
                  <DeltaBadge curr={row.sample_count} prev={prev?.sample_count} decimals={0} suffix="" />
                </td>
                {displayPeriods.map(p => {
                  const lowN = row.sample_count < MIN_RELIABLE_N;
                  const ret  = lowN ? null : row.avg_return?.[p];
                  const peak = lowN ? null : row.avg_peak?.[p];
                  const prevRet = prev?.avg_return?.[p];
                  const prevPeak = prev?.avg_peak?.[p];
                  return (
                    <React.Fragment key={p}>
                      <td className={`py-2 px-2 text-right font-mono tabular-nums text-[12px] border-l border-white/[0.04] whitespace-nowrap ${returnColor(ret)}`}>
                        {ret != null ? fmtPct(ret) : '—'}
                        <DeltaBadge curr={ret} prev={prevRet} />
                      </td>
                      <td className="py-2 px-2 text-right font-mono tabular-nums text-[12px] text-gray-500 whitespace-nowrap">
                        {peak != null ? fmtPct(peak) : '—'}
                        <DeltaBadge curr={peak} prev={prevPeak} />
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollTable>
  );
}


export default function ReturnsTab() {
  const { selectedWindowBuckets, prevByBucket, selectedWindow } = useAssessmentData();
  return (
  <>
    <SectionHeader>
      Side-adjusted EV (exit return) and MFE per period
      {selectedWindow ? ` — ${selectedWindow} window` : ''}
    </SectionHeader>
    <p className="text-[12px] text-gray-500 mb-3 leading-relaxed">
      Return = avg close-to-exit % (direction-adjusted: positive = trade worked). MFE = avg max favorable excursion within the window.
    </p>
    {selectedWindowBuckets.length === 0
      ? <p className="text-gray-500 text-sm text-center py-8">No buckets to display.</p>
      : <ReturnsTable buckets={selectedWindowBuckets} prevByBucket={prevByBucket} />
    }
  </>
  );
}
